import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Stack,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
} from '@mui/material';
import Iconify from '../components/iconify';
import useAuth from '../hooks/useAuth';
import { listPlans, changeTier, getMySubscription } from '../services/subscriptionService';
import { infoNotification, errorNotification } from '../services/notificationService';

const tierDescriptions = {
  beta: 'Early adopter access with all features included.',
  bronze: 'Essential plan for getting started.',
  silver: 'Unlock AI-powered features for your workflow.',
  gold: 'Maximum storage and all advanced features.',
};

const tierHighlightColors = {
  beta: '#2196f3',
  bronze: '#cd7f32',
  silver: '#9e9e9e',
  gold: '#ffd700',
};

function formatStorage(mb) {
  if (mb >= 1_048_576) return `${(mb / 1_048_576).toFixed(0)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
  return `${mb} MB`;
}

function formatPrice(cents) {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(0)}`;
}

const featureLabels = {
  ai_transcription: 'AI Transcription',
  ai_analysis: 'AI Analysis',
  advanced_ai: 'Advanced AI Features',
  priority_processing: 'Priority Processing',
};

const allFeatures = ['priority_processing', 'ai_transcription', 'ai_analysis', 'advanced_ai'];

export default function PlansPage() {
  const { refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    Promise.all([listPlans(), getMySubscription()])
      .then(([planData, sub]) => {
        setPlans(planData);
        setCurrentTier(sub.tier);
      })
      .catch(() => errorNotification('Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectPlan = (plan) => {
    if (plan.name === currentTier) return;
    setSelectedPlan(plan);
    setConfirmOpen(true);
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan) return;
    setChanging(true);
    try {
      await changeTier(selectedPlan.name);
      setCurrentTier(selectedPlan.name);
      await refreshUser();
      infoNotification(`Switched to ${selectedPlan.displayName} plan`);
      setConfirmOpen(false);
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to change plan');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>VPSI | Plans</title>
      </Helmet>

      <Container>
        <Stack alignItems="center" mb={5}>
          <Typography variant="h3" gutterBottom>
            Choose Your Plan
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select the plan that fits your needs. Upgrade or downgrade anytime.
          </Typography>
        </Stack>

        <Grid container spacing={3} justifyContent="center">
          {plans
            .filter((plan) => plan.name !== 'beta' || currentTier === 'beta')
            .map((plan) => {
              const isCurrent = plan.name === currentTier;
              return (
                <Grid item xs={12} sm={6} md={3} key={plan.name}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: isCurrent ? 2 : 1,
                      borderColor: isCurrent ? tierHighlightColors[plan.name] : 'divider',
                      position: 'relative',
                    }}
                  >
                    {isCurrent && (
                      <Chip
                        label="Current Plan"
                        color="primary"
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12 }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" gutterBottom sx={{ color: tierHighlightColors[plan.name] }}>
                        {plan.displayName}
                      </Typography>
                      <Box sx={{ minHeight: 120 }}>
                        <Typography variant="h4" gutterBottom>
                          {formatPrice(plan.monthlyPriceCents)}
                          {plan.monthlyPriceCents > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              /month
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ minHeight: 20 }}>
                          {plan.orgPerUserPriceCents > 0
                            ? `Org: $${(plan.orgPerUserPriceCents / 100).toFixed(0)}/user/mo`
                            : '\u00A0'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tierDescriptions[plan.name]}
                        </Typography>
                      </Box>
                      <List dense>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Iconify icon="eva:hard-drive-fill" width={20} />
                          </ListItemIcon>
                          <ListItemText primary={`${formatStorage(plan.storageLimitMb)} storage`} />
                        </ListItem>
                        {allFeatures.map((feature) => {
                          const hasFeature = plan.features.includes(feature);
                          return (
                            <ListItem key={feature} disableGutters>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Iconify
                                  icon={hasFeature ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-fill'}
                                  width={20}
                                  color={hasFeature ? 'success.main' : 'text.disabled'}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={featureLabels[feature] || feature}
                                primaryTypographyProps={hasFeature ? {} : { color: 'text.disabled' }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant={isCurrent ? 'outlined' : 'contained'}
                        fullWidth
                        disabled={isCurrent}
                        onClick={() => handleSelectPlan(plan)}
                      >
                        {isCurrent ? 'Current Plan' : 'Select Plan'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      </Container>

      {/* Confirm Change Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switch to the <strong>{selectedPlan?.displayName}</strong> plan?
            {selectedPlan?.monthlyPriceCents > 0
              ? ` You will be charged $${(selectedPlan.monthlyPriceCents / 100).toFixed(2)}/month.`
              : ' This plan is free.'}
            {' '}Your storage limit will be updated to {selectedPlan && formatStorage(selectedPlan.storageLimitMb)}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={changing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmChange} disabled={changing}>
            {changing ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
